/**
 * Vendor-agnostic helpers for a planting's free-form `CustomData` JSON blob
 * (an NVARCHAR(MAX) string on the API). The blob holds per-vendor nested subfields
 * (e.g. `calGiant`, `bonipak`); these helpers read and write a single named subfield
 * while preserving the others.
 */

/** Safely parse a CustomData JSON string; returns {} for empty/invalid input. */
export function parseCustomData(
	customData?: string | null,
): Record<string, unknown> {
	if (!customData) return {};
	try {
		const parsed = JSON.parse(customData);
		return parsed && typeof parsed === "object"
			? (parsed as Record<string, unknown>)
			: {};
	} catch {
		return {};
	}
}

/**
 * Replace a top-level subfield of a CustomData blob, preserving all sibling keys.
 * Pass undefined (or an empty object) to remove the subfield. Returns the serialized
 * blob, or undefined when it becomes empty.
 */
export function setSubfield(
	customData: string | null | undefined,
	key: string,
	value: Record<string, unknown> | undefined,
): string | undefined {
	const existing = parseCustomData(customData);

	// Rebuild without `key` (avoids a dynamic delete), then re-add it if non-empty.
	const data: Record<string, unknown> = {};
	for (const k of Object.keys(existing)) {
		if (k !== key) data[k] = existing[k];
	}
	if (value && Object.keys(value).length > 0) {
		data[key] = value;
	}

	return Object.keys(data).length > 0 ? JSON.stringify(data) : undefined;
}
